// web/src/components/PhotoPicker.tsx — "Add photos" for "Ask for a change" and "Send us photos" (portal spec 19, G7).
// Up to `max` photos from the phone's camera roll. Each one gets a short-lived upload link from the API (POST
// /v1/uploads/sas, which files it under the client's own workspace) and goes straight to storage; the caller gets the
// stored addresses and writes one `Uploaded image: <address>` line per photo into the request (the update agent reads
// those lines). `uploading` is true while any photo is still on its way, so the caller can hold its Send button.
import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { apiErrorStatus } from '../lib/errors';
import { Icon } from './Icon';

type Item = { key: string; name: string; preview: string; blobUrl: string | null; failed: boolean };

export function PhotoPicker({ max = 5, optional = true, disabled, onChange, sas }: {
  max?: number;
  optional?: boolean;
  disabled?: boolean;
  onChange: (blobUrls: string[], uploading: boolean) => void;
  /** Where the upload link comes from. Default: the client's own workspace. Admin passes the site's (spec 22). */
  sas?: (filename: string, contentType: string) => Promise<{ uploadUrl: string; blobUrl: string }>;
}) {
  const [items, setItems] = useState<Item[]>([]);
  const [off, setOff] = useState(false); // the API has no storage set up (503): say so once, not per photo
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    onChange(items.filter((i) => i.blobUrl).map((i) => i.blobUrl!), items.some((i) => !i.blobUrl && !i.failed));
  }, [items, onChange]);
  // Free the preview images when the picker goes away.
  const latest = useRef(items);
  latest.current = items;
  useEffect(() => () => latest.current.forEach((i) => URL.revokeObjectURL(i.preview)), []);

  async function upload(key: string, file: File) {
    try {
      const { uploadUrl, blobUrl } = await (sas ?? api.uploadSas)(file.name, file.type || 'image/jpeg');
      // Mock mode hands back a test address that isn't real storage: skip the PUT there.
      if (!uploadUrl.includes('blob.test')) {
        const r = await fetch(uploadUrl, { method: 'PUT', headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type }, body: file });
        if (!r.ok) throw new Error(`upload ${r.status}`);
      }
      setItems((all) => all.map((i) => (i.key === key ? { ...i, blobUrl } : i)));
    } catch (e) {
      if (apiErrorStatus(e) === 503) {
        setOff(true);
        setItems((all) => all.filter((i) => i.key !== key));
        return;
      }
      setItems((all) => all.map((i) => (i.key === key ? { ...i, failed: true } : i)));
    }
  }

  function pick(files: FileList | null) {
    if (!files) return;
    const room = Math.max(0, max - items.length);
    const added = Array.from(files).slice(0, room).map((f) => ({
      file: f,
      item: { key: `${Date.now()}-${Math.random().toString(36).slice(2)}`, name: f.name, preview: URL.createObjectURL(f), blobUrl: null, failed: false },
    }));
    setItems((all) => [...all, ...added.map((a) => a.item)]);
    for (const a of added) void upload(a.item.key, a.file);
    if (input.current) input.current.value = '';
  }

  function remove(key: string) {
    setItems((all) => {
      const gone = all.find((i) => i.key === key);
      if (gone) URL.revokeObjectURL(gone.preview);
      return all.filter((i) => i.key !== key);
    });
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-ink">Add photos <span className="font-normal text-muted">({optional ? 'optional, ' : ''}up to {max})</span></span>
      {items.length > 0 && (
        <ul className="grid grid-cols-3 gap-2 sm:grid-cols-5">
          {items.map((i) => (
            <li key={i.key} className="relative overflow-hidden rounded-md border border-hair bg-surface2">
              <img src={i.preview} alt={i.name} className={`aspect-square w-full object-cover ${i.blobUrl ? '' : 'opacity-50'}`} />
              <span className="absolute inset-x-0 bottom-0 bg-bg/80 px-1.5 py-0.5 text-[11px] text-ink">
                {i.failed ? "Didn't upload" : i.blobUrl ? 'Added' : 'Uploading…'}
              </span>
              <button type="button" onClick={() => remove(i.key)} aria-label={`Remove ${i.name}`}
                className="absolute right-0 top-0 inline-flex min-h-11 min-w-11 items-center justify-center text-ink">
                <span className="inline-flex size-7 items-center justify-center rounded-full bg-bg/90"><Icon name="x" size={14} /></span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {off && (
        <p className="text-xs text-muted">Adding photos isn't switched on yet. You can still send this, and email us the photos.</p>
      )}
      {items.length < max && !off && (
        <label className={`inline-flex min-h-11 w-fit cursor-pointer items-center gap-2 rounded-md border border-hair px-4 text-sm text-ink ${disabled ? 'pointer-events-none opacity-50' : ''}`}>
          <Icon name="image" size={16} />{items.length ? 'Add another photo' : 'Add photos'}
          <input ref={input} type="file" accept="image/*" multiple className="sr-only" disabled={disabled}
            onChange={(e) => pick(e.target.files)} />
        </label>
      )}
    </div>
  );
}
